/**
 * Document titles for the game screens.
 *
 * The game document (`index.html`, served at `/play`, and at `/` inside Discord) is kept out of
 * the index by the server's `X-Robots-Tag` and has its canonical on the landing site, so nothing here is for search engines: the title exists so a
 * player with several tabs open can tell which table is which. The static head stays the source of
 * truth for unfurls of an invite link.
 */
import { useEffect } from 'react';
import type { Screen } from '../store/store';

/** The title before there is anything more specific to say; matches `index.html`. */
export const GAME_TITLE = 'Play Spades Online — Spades Night';

/** Tab titles for the in-game screens. Short, and specific enough to tell two tables apart. */
export function screenTitle(screen: Screen, opts: { code?: string | null; handNo?: number | null } = {}): string {
  const table = opts.code ? ` · ${opts.code}` : '';
  switch (screen) {
    case 'boot':
      return GAME_TITLE;
    case 'tutorial':
      return 'First hand · Spades Night';
    case 'lobby':
      return `Lobby${table} · Spades Night`;
    case 'handEnd':
      return `Hand ${opts.handNo ?? ''} scored${table} · Spades Night`.replace('  ', ' ');
    case 'matchEnd':
      return `Match over${table} · Spades Night`;
    case 'table':
      return `Playing${table} · Spades Night`;
  }
}

/** Keep `document.title` in step with the current screen. */
export function useScreenTitle(screen: Screen, opts: { code?: string | null; handNo?: number | null } = {}): void {
  const { code, handNo } = opts;
  useEffect(() => {
    document.title = screenTitle(screen, { code, handNo });
  }, [screen, code, handNo]);
}
